import React, { useEffect, useState, useMemo } from "react";
const parseCSV = (csvText) => {
  const lines = csvText.split('\n').filter(line => line.trim());
  if (lines.length === 0) return [];
  
  const headers = lines[0].split(',').map(h => h.trim().replace(/^"|"$/g, ''));
  const results = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = lines[i].split(',').map(v => v.trim().replace(/^"|"$/g, ''));
    if (values.length === headers.length) {
      const obj = {};
      headers.forEach((header, index) => {
        obj[header] = values[index] || '';
      });
      results.push(obj);
    }
  }
  
  return results;
};

// ==========================================
// 1. SHARED CONSTANTS & UTILITIES
// ==========================================
const formatCurrency = (amount) => {
  if (amount === undefined || amount === null || Number.isNaN(amount) || !Number.isFinite(amount)) return '₹0';
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};

const formatCurrencyShort = (amount) => {
  if (amount === undefined || amount === null || Number.isNaN(amount) || !Number.isFinite(amount)) return '₹0';
  if (amount >= 100000) return `₹${(amount / 100000).toFixed(1)}L`;
  if (amount >= 1000) return `₹${(amount / 1000).toFixed(0)}k`;
  return formatCurrency(amount);
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return dateString;
  }
};

// ==========================================
// 2. INFO TOOLTIP COMPONENT
// ==========================================
const InfoTooltip = ({ content, position = "top" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const isVisible = isOpen || isHovered;

  const positionClasses = {
    top: "bottom-full left-1/2 transform -translate-x-1/2 mb-2",
    bottom: "top-full left-1/2 transform -translate-x-1/2 mt-2",
    left: "right-full top-1/2 transform -translate-y-1/2 mr-2",
    right: "left-full top-1/2 transform -translate-y-1/2 ml-2",
  };

  const arrowClasses = {
    top: "absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800 border-r border-b border-slate-700",
    bottom: "absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800 border-l border-t border-slate-700",
    left: "absolute right-0 top-1/2 transform translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800 border-r border-t border-slate-700",
    right: "absolute left-0 top-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-slate-800 border-l border-b border-slate-700",
  };

  return (
    <div className="relative inline-flex items-center ml-1">
      <button
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`
          inline-flex items-center justify-center 
          w-4 h-4 rounded-full 
          transition-all duration-200
          ${isVisible 
            ? 'bg-blue-500 text-white ring-2 ring-blue-400 ring-offset-1 scale-110' 
            : 'bg-gray-200 text-gray-600 hover:bg-blue-100 hover:text-blue-600 hover:ring-2 hover:ring-blue-200'
          }
          focus:outline-none focus:ring-2 focus:ring-blue-400 focus:ring-offset-2
        `}
        aria-label="Information"
        aria-expanded={isVisible}
        type="button"
      >
        <i className="ti ti-info-circle text-[10px]" />
      </button>
      
      {isVisible && (
        <div 
          className={`absolute z-[9999] ${positionClasses[position]} w-64 bg-slate-800 text-white rounded-lg shadow-2xl p-3 text-xs pointer-events-auto animate-in fade-in slide-in-from-bottom-2 duration-200 border border-slate-700`}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1">{content}</div>
            <button 
              onClick={() => setIsOpen(false)}
              className="text-white/50 hover:text-white transition-colors p-0.5 flex-shrink-0"
              aria-label="Close tooltip"
              type="button"
            >
              <i className="ti ti-x text-sm" />
            </button>
          </div>
          <div className={arrowClasses[position]} />
        </div>
      )}
    </div>
  );
};

// ==========================================
// 3. DATE RANGE PICKER COMPONENT (CUSTOM)
// ==========================================
const DateRangePicker = ({ dateRange, setDateRange, onApply }) => {
  const [localStartDate, setLocalStartDate] = useState(dateRange.startDate);
  const [localEndDate, setLocalEndDate] = useState(dateRange.endDate);
  const [isOpen, setIsOpen] = useState(false);

  // Quick date range options
  const quickRanges = [
    { label: 'Today', days: 0 },
    { label: 'Yesterday', days: 1 },
    { label: 'Last 7 Days', days: 7 },
    { label: 'Last 30 Days', days: 30 },
    { label: 'This Month', days: 'month' },
    { label: 'Last Month', days: 'lastMonth' },
  ];

  const applyQuickRange = (days) => {
    const end = new Date();
    let start = new Date();
    
    if (days === 'month') {
      start = new Date(end.getFullYear(), end.getMonth(), 1);
    } else if (days === 'lastMonth') {
      start = new Date(end.getFullYear(), end.getMonth() - 1, 1);
      end.setDate(0);
    } else {
      start.setDate(end.getDate() - days);
    }
    
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    };
    
    const startStr = formatDate(start);
    const endStr = formatDate(end);
    
    setLocalStartDate(startStr);
    setLocalEndDate(endStr);
    setDateRange({ startDate: startStr, endDate: endStr });
    setIsOpen(false);
    if (onApply) onApply();
  };

  const handleApply = () => {
    if (localStartDate && localEndDate) {
      setDateRange({ startDate: localStartDate, endDate: localEndDate });
      setIsOpen(false);
      if (onApply) onApply();
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="px-4 py-2 bg-white hover:bg-slate-50 text-slate-700 rounded-xl border border-slate-200 shadow-xs flex items-center justify-center gap-2 text-sm font-medium transition-all"
      >
        <i className="ti ti-calendar text-blue-500" />
        <span className="hidden sm:inline">
          {new Date(dateRange.startDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })} - 
          {new Date(dateRange.endDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
        <i className={`ti ti-chevron-down text-xs transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 bg-white rounded-xl border border-slate-200 shadow-xl z-50 p-4 w-[500px] animate-in slide-in-from-top-2 duration-200">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Quick Select</h4>
              <div className="space-y-1">
                {quickRanges.map((range) => (
                  <button
                    key={range.label}
                    onClick={() => applyQuickRange(range.days)}
                    className="w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50 rounded-lg transition-colors text-slate-700"
                  >
                    {range.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wider">Custom Range</h4>
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">Start Date</label>
                <input
                  type="date"
                  value={localStartDate}
                  onChange={(e) => setLocalStartDate(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <div>
                <label className="text-[10px] text-gray-500 block mb-1">End Date</label>
                <input
                  type="date"
                  value={localEndDate}
                  onChange={(e) => setLocalEndDate(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200"
                />
              </div>
              <button
                onClick={handleApply}
                className="w-full px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-xs font-semibold transition-colors"
              >
                Apply Range
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==========================================
// 4. FILTERS BAR COMPONENT
// ==========================================
const FiltersBar = ({
  searchTerm, setSearchTerm,
  statusFilter, setStatusFilter,
  sourceFilter, setSourceFilter,
  sortBy, setSortBy,
  uniqueStatuses, uniqueSources,
  handleExportCSV, disableExport
}) => {
  const hasActiveFilters = statusFilter !== "all" || sourceFilter !== "all" || searchTerm.trim() !== "";

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6 shadow-sm space-y-4">
      <div className="w-full">
        <div className="relative">
          <label htmlFor="search-operations" className="sr-only">Search by booking code</label>
          <i className="ti ti-search absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-sm" />
          <input
            id="search-operations"
            type="text"
            placeholder="Search by booking code, vehicle name, registration or city..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-blue-300"
          />
        </div>
      </div>
      
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative inline-flex items-center">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 pr-8"
              aria-label="Filter by booking status"
            >
              <option value="all">All statuses</option>
              {uniqueStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
            <InfoTooltip content="Filter bookings by their current status. Select a specific status to view only those bookings." position="bottom" />
          </div>
          
          <div className="relative inline-flex items-center">
            <select
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 pr-8"
              aria-label="Filter by city"
            >
              <option value="all">All cities</option>
              {uniqueSources.map(source => (
                <option key={source} value={source}>{source}</option>
              ))}
            </select>
            <InfoTooltip content="Filter bookings by city/hub location. Select a specific city to view bookings from that location." position="bottom" />
          </div>
          
          <div className="relative inline-flex items-center">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-200 pr-8"
              aria-label="Sort operations"
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="highest-revenue">Highest revenue</option>
            </select>
            <InfoTooltip content="Sort bookings by date or revenue amount. Choose how you want to order the results." position="bottom" />
          </div>
        </div>

        <div className="relative inline-flex items-center">
          <button
            onClick={handleExportCSV}
            disabled={disableExport}
            className={`px-4 py-2 rounded-lg flex items-center gap-2 text-sm font-medium transition-all ${
              disableExport ? 'bg-slate-100 text-slate-400 cursor-not-allowed' : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
            }`}
          >
            <i className="ti ti-download text-sm" />
            Export CSV
          </button>
          <InfoTooltip content="Download the current filtered data as a CSV file. All active filters will be applied to the export." position="left" />
        </div>
      </div>
      
      {hasActiveFilters && (
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
          <span className="text-xs text-gray-400 mr-1">Active filters:</span>
          {statusFilter !== "all" && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium border border-slate-200">
              Status: {statusFilter}
              <button onClick={() => setStatusFilter("all")} className="ml-1 text-slate-400 hover:text-blue-600 focus:outline-none font-bold" aria-label="Remove status filter">×</button>
            </span>
          )}
          {sourceFilter !== "all" && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium border border-slate-200">
              City: {sourceFilter}
              <button onClick={() => setSourceFilter("all")} className="ml-1 text-slate-400 hover:text-blue-600 focus:outline-none font-bold" aria-label="Remove city filter">×</button>
            </span>
          )}
          {searchTerm.trim() !== "" && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-slate-100 text-slate-700 rounded text-xs font-medium border border-slate-200">
              Search: "{searchTerm}"
              <button onClick={() => setSearchTerm("")} className="ml-1 text-slate-400 hover:text-blue-600 focus:outline-none font-bold" aria-label="Remove search term">×</button>
            </span>
          )}
          <button 
            onClick={() => { setStatusFilter("all"); setSourceFilter("all"); setSearchTerm(""); }}
            className="text-xs text-blue-500 hover:text-blue-600 font-medium ml-auto"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
};

// ==========================================
// 5. STATISTICS CARDS COMPONENT - FULLY UPDATED
// ==========================================
const StatisticsCards = ({ stats, monthlyTrend, deliveryStats, penaltiesSummary }) => {
  const momTrendPercentage = useMemo(() => {
    if (monthlyTrend.length < 2) return { value: 0, isPositive: true };
    const latest = monthlyTrend[monthlyTrend.length - 1]?.amount || 0;
    const previous = monthlyTrend[monthlyTrend.length - 2]?.amount || 0;
    
    if (previous === 0) {
      return { value: latest > 0 ? 100 : 0, isPositive: latest >= 0 };
    }
    const diff = ((latest - previous) / previous) * 100;
    return { value: Math.abs(diff).toFixed(0), isPositive: diff >= 0 };
  }, [monthlyTrend]);

  return (
    <div className="space-y-5 mb-8">
      {/* Row 1: Main Stats */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        <div className="md:col-span-5 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl p-6 text-white shadow-lg relative overflow-hidden">
          <div className="flex items-center gap-2">
            <p className="text-sm opacity-80 flex items-center gap-2">
              <i className="ti ti-currency-rupee" /> Total fleet revenue
            </p>
            <InfoTooltip content="Total revenue generated from all bookings including extra KM charges, extra hour charges, delivery charges, and pickup charges." position="bottom"/>
          </div>
          <div className="flex items-baseline gap-3 flex-wrap">
            <h2 className="text-4xl font-semibold mt-2 tracking-tight">
              {formatCurrency(stats.totalRevenue)}
            </h2>
            {monthlyTrend.length >= 2 && (
              <span className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-white/20 text-white" title="Month over Month Trend Indicator">
                {momTrendPercentage.isPositive ? '↑' : '↓'} {momTrendPercentage.value}% MoM
              </span>
            )}
          </div>
          <div className="mt-5 flex gap-6">
            <div>
              <p className="text-xs opacity-70">Total bookings</p>
              <p className="text-xl font-medium">{stats.totalBookings}</p>
            </div>
            <div>
              <p className="text-xs opacity-70">Active fleet</p>
              <p className="text-xl font-medium">{stats.activeFleet}</p>
            </div>
            <div>
              <p className="text-xs opacity-70">Utilization rate</p>
              <p className="text-xl font-medium">{stats.utilizationRate.toFixed(0)}%</p>
            </div>
          </div>
        </div>
        
        <div className="md:col-span-7 grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl p-4 border border-slate-200 border-l-4 border-l-emerald-500 shadow-sm flex flex-col justify-between">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <span className="p-1.5 bg-emerald-50 text-emerald-600 rounded-md"><i className="ti ti-truck-delivery" /></span>
              <span className="font-medium">Delivery Charges</span>
              <InfoTooltip content="Total delivery charges collected from all bookings. Includes hub delivery and home delivery charges." />
            </div>
            <p className="text-2xl font-semibold text-slate-800 mt-2">{formatCurrency(deliveryStats.totalDeliveryCharges)}</p>
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>Pending: {deliveryStats.totalPendingDelivery}</span>
              <span className="text-red-500">Late: {deliveryStats.totalLateDelivery}</span>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-slate-200 border-l-4 border-l-amber-500 shadow-sm flex flex-col justify-between">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <span className="p-1.5 bg-amber-50 text-amber-600 rounded-md"><i className="ti ti-package" /></span>
              <span className="font-medium">Pickup Charges</span>
              <InfoTooltip content="Total pickup charges collected from all bookings. Includes hub pickup and home pickup charges." />
            </div>
            <p className="text-2xl font-semibold text-slate-800 mt-2">{formatCurrency(deliveryStats.totalPickCharges)}</p>
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>Pending: {deliveryStats.totalPickupPending}</span>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border border-slate-200 border-l-4 border-l-blue-500 shadow-sm flex flex-col justify-between">
            <div className="flex items-center gap-2 text-gray-500 text-sm">
              <span className="p-1.5 bg-blue-50 text-blue-600 rounded-md"><i className="ti ti-building" /></span>
              <span className="font-medium">Active Hubs</span>
              <InfoTooltip content="Number of unique cities/hubs with active bookings in the selected date range." />
            </div>
            <p className="text-2xl font-semibold text-blue-600 mt-2">{stats.activeHubs}</p>
            <p className="text-xs text-gray-400 mt-1">Fleet size: {stats.fleetSize}</p>
          </div>
        </div>
      </div>

      {/* Row 2: Penalties Summary */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
        <div className="md:col-span-12 bg-white rounded-xl p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="font-semibold text-slate-700 text-sm flex items-center gap-2">
              <i className="ti ti-alert-triangle text-red-500" /> Penalties & Violations Summary
            </h3>
            <InfoTooltip content="Summary of all penalties and violations across all bookings. This includes overspeed, damage, fuel, fastag, and traffic violation charges." />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-7 gap-3">
            <div className="bg-red-50 rounded-lg p-3 border border-red-100">
              <p className="text-xs text-gray-500">Over Speed</p>
              <p className="text-lg font-bold text-red-600">{formatCurrency(penaltiesSummary.totalOverSpeed)}</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-3 border border-orange-100">
              <p className="text-xs text-gray-500">Damage</p>
              <p className="text-lg font-bold text-orange-600">{formatCurrency(penaltiesSummary.totalDamage)}</p>
            </div>
            <div className="bg-yellow-50 rounded-lg p-3 border border-yellow-100">
              <p className="text-xs text-gray-500">Fuel</p>
              <p className="text-lg font-bold text-yellow-600">{formatCurrency(penaltiesSummary.totalFuel)}</p>
            </div>
            <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
              <p className="text-xs text-gray-500">Fastag</p>
              <p className="text-lg font-bold text-purple-600">{formatCurrency(penaltiesSummary.totalFastag)}</p>
            </div>
            <div className="bg-pink-50 rounded-lg p-3 border border-pink-100">
              <p className="text-xs text-gray-500">Traffic Violations</p>
              <p className="text-lg font-bold text-pink-600">{formatCurrency(penaltiesSummary.totalTrafficViolations)}</p>
            </div>
            <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
              <p className="text-xs text-gray-500">Downtime Charges</p>
              <p className="text-lg font-bold text-indigo-600">{formatCurrency(penaltiesSummary.totalDowntimeCharges)}</p>
            </div>
            <div className="bg-red-50 rounded-lg p-3 border border-red-200">
              <p className="text-xs text-gray-500">Late Deliveries</p>
              <p className="text-lg font-bold text-red-600">{penaltiesSummary.totalLateDeliveries}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ==========================================
// 6. CHARTS ROW COMPONENT
// ==========================================
const ChartsRow = ({ hubDistribution, statusDistribution }) => {
  const [activeTab, setActiveTab] = useState("status");
  const [hoveredItem, setHoveredItem] = useState(null);

  const totalAmount = hubDistribution.reduce((sum, h) => sum + h.amount, 0);

  const HubDistributionView = () => {
    const maxAmount = Math.max(...hubDistribution.map(h => h.amount), 1);
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-gray-500">Revenue distribution by city</span>
          <InfoTooltip content="Shows revenue generated from each city/hub. The bar length represents the revenue amount, and the percentage shows the share of total revenue." />
        </div>
        {hubDistribution.slice(0, 6).map((hub) => {
          const barWidth = (hub.amount / maxAmount) * 100;
          const hubPercent = totalAmount > 0 ? ((hub.amount / totalAmount) * 100).toFixed(1) : 0;
          
          return (
            <div 
              key={hub.name} 
              className="relative group cursor-pointer p-1.5 rounded-lg hover:bg-slate-50 transition-all"
              onMouseEnter={() => setHoveredItem(hub.name)}
              onMouseLeave={() => setHoveredItem(null)}
            >
              <div className="flex justify-between text-xs mb-0.5">
                <span className="font-medium flex items-center gap-2 text-slate-700">
                  <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: hub.color }}></span>
                  <span className="truncate max-w-[120px]">{hub.name}</span>
                  <span className="text-gray-400 font-normal text-[10px]">({hub.count} bookings)</span>
                </span>
                <span className="font-semibold text-slate-700">{formatCurrencyShort(hub.amount)}</span>
              </div>
              
              <div className="h-6 bg-gray-100 rounded-md overflow-hidden relative">
                <div 
                  className="h-full rounded-md transition-all duration-500 flex items-center px-2"
                  style={{ 
                    width: `${Math.max(barWidth, 4)}%`, 
                    backgroundColor: hub.color,
                    opacity: 0.85
                  }}
                >
                  <span className="text-white text-[10px] font-bold">
                    {hubPercent}%
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const StatusDistributionView = () => {
    const maxCount = Math.max(...statusDistribution.map(s => s.count), 1);
    return (
      <div className="space-y-3">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-xs text-gray-500">Booking status breakdown</span>
          <InfoTooltip content="Shows the distribution of bookings by their current status. Each status is color-coded for easy identification." />
        </div>
        {statusDistribution.map((status) => {
          const barWidth = (status.count / maxCount) * 100;
          return (
            <div key={status.name} className="space-y-1">
              <div className="flex justify-between text-xs">
                <span className="font-medium flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: status.color }}></span>
                  {status.name}
                </span>
                <span className="text-gray-500">
                  {status.count} bookings · {formatCurrencyShort(status.amount)}
                </span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ width: `${barWidth}%`, backgroundColor: status.color }}
                />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-8">
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-slate-800 flex items-center gap-2 text-sm">
            <i className="ti ti-building text-blue-500" /> Hub performance
          </h3>
        </div>
        <HubDistributionView />
      </div>
      
      <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-medium text-slate-800 flex items-center gap-2 text-sm">
            <i className="ti ti-chart-pie text-indigo-500" /> Booking status distribution
          </h3>
          <div className="inline-flex rounded-md p-0.5 bg-slate-100 border border-slate-200">
            <button 
              onClick={() => setActiveTab("hub")}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${activeTab === "hub" ? "bg-white shadow-xs text-blue-600 font-semibold" : "text-gray-500 hover:text-gray-700"}`}
            >
              Hubs
            </button>
            <button 
              onClick={() => setActiveTab("status")}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-all ${activeTab === "status" ? "bg-white shadow-xs text-blue-600 font-semibold" : "text-gray-500 hover:text-gray-700"}`}
            >
              Status
            </button>
          </div>
        </div>
        {activeTab === "hub" ? <HubDistributionView /> : <StatusDistributionView />}
      </div>
    </div>
  );
};

// ==========================================
// 7. OPERATIONAL METRICS FUNNEL
// ==========================================
const OperationsFunnel = ({ funnelData }) => {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6 shadow-sm">
      <div className="flex items-center justify-between mb-5">
        <h3 className="font-medium text-slate-800 flex items-center gap-2">
          <i className="ti ti-chart-arrows" /> Operational pipeline 
          <InfoTooltip content="Shows the booking lifecycle from total bookings to completed trips. Each stage shows the count and conversion rate." />
          <span className="text-xs font-normal text-blue-600 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded-full">Live metrics</span>
        </h3>
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-slate-700 rounded-full" /> Volume</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-blue-500 rounded-full" /> Active</span>
          <span className="flex items-center gap-1"><span className="w-2 h-2 bg-emerald-500 rounded-full" /> Completed</span>
        </div>
      </div>
      
      <div className="space-y-4">
        {funnelData.map((stage, idx) => {
          let conversionAnnotation = null;
          if (idx > 0 && funnelData[idx - 1].count > 0) {
            const conversionRate = ((stage.count / funnelData[idx - 1].count) * 100).toFixed(0);
            conversionAnnotation = `${funnelData[idx - 1].stage} → ${stage.stage}: ${conversionRate}% rate`;
          }

          return (
            <div key={idx} className="space-y-1 relative">
              {conversionAnnotation && (
                <div className="text-[10px] font-bold text-gray-400 bg-slate-50 px-2 py-0.5 rounded border border-dashed border-slate-200 inline-block -mt-1 mb-1">
                  <i className="ti ti-arrow-forward mr-1 text-[9px]" /> {conversionAnnotation}
                </div>
              )}
              <div className="flex justify-between text-xs">
                <span className="text-gray-600 font-medium">{stage.stage}</span>
                <span className="font-semibold text-slate-700">{stage.count} bookings</span>
              </div>
              <div className="relative">
                <div 
                  className="h-6 rounded-l-md transition-all duration-500 flex items-center px-2 shadow-xs"
                  style={{ width: `${Math.max(stage.percentage, 8)}%`, backgroundColor: stage.color, opacity: 0.85 }}
                >
                  <span className="text-white text-[10px] font-bold">{stage.percentage.toFixed(0)}%</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ==========================================
// 8. TOP PERFORMING HUBS WIDGET
// ==========================================
const TopHubsWidget = ({ topHubs }) => {
  if (topHubs.length === 0) return null;

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 mb-6 shadow-sm">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-medium text-slate-800 flex items-center gap-2">
          <i className="ti ti-crown text-amber-500" /> Top performing hubs
          <InfoTooltip content="Shows the top 5 cities/hubs by revenue. The growth percentage indicates the revenue growth compared to the previous hub." />
        </h3>
        <span className="text-xs text-gray-400">Revenue leaders</span>
      </div>
      <div className="space-y-2.5">
        {topHubs.map((hub, idx) => (
          <div key={hub.id} className="flex justify-between items-center p-2 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-100 transition-all">
            <div className="flex items-center gap-3 min-w-0">
              <span className="text-xs font-mono font-bold text-slate-400 w-5">#{idx + 1}</span>
              <div className="min-w-0">
                <p className="font-semibold text-sm text-slate-700 truncate">{hub.name}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-[11px] text-gray-400 font-medium">{hub.bookings} bookings</span>
                  <span className="w-1 h-1 rounded-full bg-gray-300" />
                  <span className="text-[11px] text-gray-400">{formatCurrencyShort(hub.avgRevenue)} avg</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              <span className="font-bold text-blue-600 text-sm">{formatCurrency(hub.revenue)}</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${hub.growth >= 0 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                {hub.growth >= 0 ? '↑' : '↓'} {Math.abs(hub.growth)}%
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ==========================================
// 9. OPERATIONS DATA TABLE - FULLY ENHANCED
// ==========================================
const OperationsTable = ({ bookings, expandedRowId, toggleRowExpansion, handleRowKeyDown, onBookingClick }) => {
  return (
    <div className="divide-y divide-slate-100">
      {bookings.map((booking) => {
        const isExpanded = expandedRowId === booking.id;
        const totalRevenue = (booking.extraKmCharges || 0) + (booking.extraHrCharges || 0) + (booking.deliveryCharges || 0) + (booking.pickupCharges || 0);
        const totalPenalties = (booking.overSpeed || 0) + (booking.damage || 0) + (booking.fuel || 0) + (booking.fasTag || 0) + (booking.trafficViolations || 0) + (booking.downtimeCharges || 0);
        
        return (
          <React.Fragment key={booking.id}>
            <div 
              className={`px-5 py-4 hover:bg-slate-50 transition-all cursor-pointer outline-none focus:bg-slate-50 ${isExpanded ? 'bg-slate-50 font-medium' : ''}`}
              onClick={() => toggleRowExpansion(booking.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleRowExpansion(booking.id);
                }
              }}
              role="button"
              tabIndex={0}
              aria-expanded={isExpanded}
              aria-label={`${isExpanded ? 'Collapse' : 'Expand'} details for booking ${booking.bookingCode}`}
            >
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span 
                      className="font-bold text-slate-800 text-sm tracking-tight hover:text-blue-600 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        onBookingClick(booking);
                      }}
                    >
                      {booking.bookingCode}
                    </span>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      booking.bookingStatus === 'Trip Completed' ? 'bg-emerald-100 text-emerald-700' :
                      booking.bookingStatus === 'Booking Confirmed' ? 'bg-blue-100 text-blue-700' :
                      booking.bookingStatus === 'Enroute' ? 'bg-amber-100 text-amber-700' :
                      booking.bookingStatus === 'Refund Pending' ? 'bg-red-100 text-red-700' :
                      booking.bookingStatus === 'Booking Extented' ? 'bg-teal-100 text-teal-700' :
                      booking.bookingStatus === 'Booking Rescheduled' ? 'bg-pink-100 text-pink-700' :
                      booking.bookingStatus === 'Document Pending' ? 'bg-purple-100 text-purple-700' :
                      booking.bookingStatus === 'Verification Pending' ? 'bg-orange-100 text-orange-700' :
                      booking.bookingStatus === 'Booking Closed' ? 'bg-slate-100 text-slate-700' :
                      'bg-slate-100 text-slate-700'
                    }`}>
                      {booking.bookingStatus || 'N/A'}
                    </span>
                    {/* Added Total KM badge */}
                    {booking.tripOdometer > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">
                        <i className="ti ti-ruler-2 mr-1" /> {booking.tripOdometer} km
                      </span>
                    )}
                    {booking.isLateDelivery === 'Yes' && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">
                        <i className="ti ti-clock mr-1" /> Late: {booking.lateDeliveryTime}h
                      </span>
                    )}
                    {totalPenalties > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-600 border border-red-200">
                        <i className="ti ti-alert-triangle mr-1" /> Penalties: {formatCurrencyShort(totalPenalties)}
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-gray-400 mt-1 font-normal flex-wrap">
                    <span>City: {booking.city || 'N/A'}</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <span>Vehicle: {booking.vehicleName || 'N/A'}</span>
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <span>{booking.vehicleRegistrationNo || 'N/A'}</span>
                    {booking.fleetExecutiveName && (
                      <>
                        <span className="w-1 h-1 rounded-full bg-gray-300" />
                        <span className="text-blue-600">Exec: {booking.fleetExecutiveName}</span>
                      </>
                    )}
                    <span className="w-1 h-1 rounded-full bg-gray-300" />
                    <span className="text-gray-400">{booking.deliveryType || 'N/A'} / {booking.pickupType || 'N/A'}</span>
                  </div>
                </div>
                
                <div className="hidden md:block flex-shrink-0 text-xs text-gray-400 font-normal">
                  <div>{formatDateTime(booking.bookFrom).split(',')[0]}</div>
                  <div className="text-[10px]">{booking.bookingHrs || 0}h · {booking.totalNoOfDays || 0}d</div>
                </div>
                
                <div className="flex-shrink-0 text-right">
                  <div className="text-lg font-bold text-blue-600 tracking-tight">
                    {formatCurrency(totalRevenue)}
                  </div>
                  {/* Added KM display in the right section */}
                  <div className="text-[10px] text-gray-400">
                    {booking.tripOdometer > 0 && (
                      <span className="inline-flex items-center gap-1">
                        <i className="ti ti-ruler-2 text-[10px]" /> {booking.tripOdometer} km
                      </span>
                    )}
                    {/* {booking.extraKmCharges > 0 && ` · +${formatCurrencyShort(booking.extraKmCharges)} extra`} */}
                    {/* {booking.extraHrCharges > 0 && ` · +${formatCurrencyShort(booking.extraHrCharges)} hr`} */}
                  </div>
                  {totalPenalties > 0 && (
                    <div className="text-[10px] text-red-500 font-medium">
                      Penalties: {formatCurrencyShort(totalPenalties)}
                    </div>
                  )}
                </div>
                
                <div className="flex-shrink-0 text-gray-400">
                  <i className={`ti ${isExpanded ? 'ti-chevron-up' : 'ti-chevron-down'} text-sm`} />
                </div>
              </div>
            </div>
            
            {isExpanded && (
              <div className="px-5 py-5 bg-slate-50 border-t border-b border-slate-100 animate-in fade-in duration-200">
                <div className="grid grid-cols-12 gap-5">
                  {/* Column 1: Trip Details */}
                  <div className="col-span-12 md:col-span-4">
                    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs">
                      <h4 className="font-semibold text-slate-700 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                        <i className="ti ti-receipt text-gray-400" /> Trip Details
                        <InfoTooltip content="Detailed trip information including vehicle, timing, and distance metrics." />
                      </h4>
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Vehicle</span>
                          <span className="font-medium text-slate-700">{booking.vehicleName || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Registration</span>
                          <span className="font-medium text-slate-700">{booking.vehicleRegistrationNo || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Fleet Executive</span>
                          <span className="font-medium text-slate-700">{booking.fleetExecutiveName || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Booking Hours</span>
                          <span className="font-medium text-slate-700">{booking.bookingHrs || 0}h</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Total Days</span>
                          <span className="font-medium text-slate-700">{booking.totalNoOfDays || 0}d</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Free KM</span>
                          <span className="font-medium text-slate-700">{booking.overallFreeKm || 0} km</span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="text-gray-500">Total KM</span>
                          <span className="font-medium text-slate-700">{booking.tripOdometer || 0} km</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Column 2: Odometer & Fuel Details */}
                  <div className="col-span-12 md:col-span-4">
                    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs">
                      <h4 className="font-semibold text-slate-700 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                        <i className="ti ti-speedometer text-gray-400" /> Odometer & Fuel
                        <InfoTooltip content="Start and end odometer readings with fuel levels. Trip odometer shows the total distance traveled." />
                      </h4>
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Start Odometer</span>
                          <span className="font-medium text-slate-700">{booking.tripStartOdometer || 0} km</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">End Odometer</span>
                          <span className="font-medium text-slate-700">{booking.tripEndOdometer || 0} km</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Start Fuel Level</span>
                          <span className="font-medium text-slate-700">{booking.tripStartFuelLevel || 'N/A'}%</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">End Fuel Level</span>
                          <span className="font-medium text-slate-700">{booking.tripEndFuelLevel || 'N/A'}%</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Actual Trip Start</span>
                          <span className="font-medium text-slate-700">{booking.actualTripStart ? formatDateTime(booking.actualTripStart) : 'N/A'}</span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="text-gray-500">Late Delivery</span>
                          <span className={`font-medium ${booking.isLateDelivery === 'Yes' ? 'text-red-600' : 'text-emerald-600'}`}>
                            {booking.isLateDelivery === 'Yes' ? `${booking.lateDeliveryTime}h late` : 'On Time'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  {/* Column 3: Financial Breakdown & Penalties */}
                  <div className="col-span-12 md:col-span-4">
                    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs">
                      <h4 className="font-semibold text-slate-700 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                        <i className="ti ti-chart-pie text-gray-400" /> Financial Breakdown
                        {booking.extraKms > 0 && (
                          <span className="text-[10px] font-normal text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-200">
                            Extra Km : {booking.extraKms}
                          </span>
                        )}
                        {booking.extraHrs > 0 && (
                          <span className="text-[10px] font-normal text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200">
                            Extra Hrs : {booking.extraHrs}
                          </span>
                        )}
                        <InfoTooltip content="Complete financial breakdown including revenue components and all penalties." />
                      </h4>
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Extra KM Charges</span>
                          <span className="font-medium text-slate-700">{formatCurrency(booking.extraKmCharges || 0)}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Extra Hour Charges</span>
                          <span className="font-medium text-slate-700">{formatCurrency(booking.extraHrCharges || 0)}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Delivery Charges</span>
                          <span className="font-medium text-slate-700">{formatCurrency(booking.deliveryCharges || 0)}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Pickup Charges</span>
                          <span className="font-medium text-slate-700">{formatCurrency(booking.pickupCharges || 0)}</span>
                        </div>
                        <div className="flex justify-between py-2 border-t-2 border-slate-200 font-bold">
                          <span className="text-slate-700">Total Revenue</span>
                          <span className="text-blue-600">{formatCurrency(totalRevenue)}</span>
                        </div>
                      </div>
                    </div>
                    
                    {/* Penalties Sub-section */}
                    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs mt-3">
                      <h4 className="font-semibold text-slate-700 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                        <i className="ti ti-alert-triangle text-red-400" /> Penalties
                        <InfoTooltip content="All penalty charges applied to this booking including overspeed, damage, fuel, fastag, and traffic violations." />
                      </h4>
                      <div className="space-y-2 text-xs">
                        {booking.overSpeed > 0 && (
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-gray-500">Over Speed</span>
                            <span className="font-medium text-red-500">{formatCurrency(booking.overSpeed)}</span>
                          </div>
                        )}
                        {booking.damage > 0 && (
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-gray-500">Damage</span>
                            <span className="font-medium text-red-500">{formatCurrency(booking.damage)}</span>
                          </div>
                        )}
                        {booking.fuel > 0 && (
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-gray-500">Fuel</span>
                            <span className="font-medium text-red-500">{formatCurrency(booking.fuel)}</span>
                          </div>
                        )}
                        {booking.fasTag > 0 && (
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-gray-500">Fastag</span>
                            <span className="font-medium text-red-500">{formatCurrency(booking.fasTag)}</span>
                          </div>
                        )}
                        {booking.trafficViolations > 0 && (
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-gray-500">Traffic Violations</span>
                            <span className="font-medium text-red-500">{formatCurrency(booking.trafficViolations)}</span>
                          </div>
                        )}
                        {booking.downtimeCharges > 0 && (
                          <div className="flex justify-between py-1 border-b border-slate-100">
                            <span className="text-gray-500">Downtime Charges</span>
                            <span className="font-medium text-red-500">{formatCurrency(booking.downtimeCharges)}</span>
                          </div>
                        )}
                        {totalPenalties === 0 ? (
                          <div className="text-center py-2 text-emerald-600 font-medium">
                            <i className="ti ti-check mr-1" /> No penalties
                          </div>
                        ) : (
                          <div className="flex justify-between py-2 border-t-2 border-red-200 font-bold">
                            <span className="text-red-700">Total Penalties</span>
                            <span className="text-red-600">{formatCurrency(totalPenalties)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Address & Comments Section */}
                <div className="grid grid-cols-12 gap-5 mt-4">
                  <div className="col-span-12 md:col-span-6">
                    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs">
                      <h4 className="font-semibold text-slate-700 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                        <i className="ti ti-map-pin text-gray-400" /> Addresses
                        <InfoTooltip content="Delivery and pickup addresses for this booking. Shows the type and location details." />
                      </h4>
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Delivery Type</span>
                          <span className="font-medium text-slate-700">{booking.deliveryType || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Delivery Address</span>
                          <span className="font-medium text-slate-700 text-right max-w-[200px] truncate">{booking.deliveryAddress || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Pickup Type</span>
                          <span className="font-medium text-slate-700">{booking.pickupType || 'N/A'}</span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="text-gray-500">Pickup Address</span>
                          <span className="font-medium text-slate-700 text-right max-w-[200px] truncate">{booking.pickupAddress || 'N/A'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="col-span-12 md:col-span-6">
                    <div className="bg-white rounded-xl p-4 border border-slate-200 shadow-xs">
                      <h4 className="font-semibold text-slate-700 text-xs uppercase tracking-wider mb-3 flex items-center gap-2">
                        <i className="ti ti-message text-gray-400" /> Comments
                        <InfoTooltip content="Comments recorded at the start and end of the trip by the fleet executive." />
                      </h4>
                      <div className="space-y-2 text-xs">
                        <div className="flex justify-between py-1 border-b border-slate-100">
                          <span className="text-gray-500">Start Comment</span>
                          <span className="font-medium text-slate-700 italic">"{booking.commentsAtStart || 'No comment'}"</span>
                        </div>
                        <div className="flex justify-between py-1">
                          <span className="text-gray-500">End Comment</span>
                          <span className="font-medium text-slate-700 italic">"{booking.commentsAtEnd || 'No comment'}"</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ==========================================
// 10. MAIN DASHBOARD COMPONENT - FULLY UPDATED
// ==========================================
const FleetOperationDashboard = () => {
  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");
  
  const [allBookings, setAllBookings] = useState([]);
  const [analyticsData, setAnalyticsData] = useState([]);
  const [apiDataValues, setApiDataValues] = useState({
    totalDeliveryCharges: 0,
    totalPickCharges: 0,
    totalPendingDelivery: 0,
    totalPickupPending: 0,
    totalLateDelivery: 0,
    dataListCount: 0
  });
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [apiError, setApiError] = useState(false);
  
  const [statusFilter, setStatusFilter] = useState("all");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [toast, setToast] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(new Date());

  const [dateRange, setDateRange] = useState({
    startDate: '2026-04-01T00:00',
    endDate: '2026-05-30T23:59'
  });

  const [visibleCount, setVisibleCount] = useState(10);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  // Fetch fleet operation data
  const fetchFleetData = async () => {
    try {
      setLoading(true);
      setApiError(false);
      
      // 1. Fetch fleet operation dashboard data (main data)
      const dashboardParams = new URLSearchParams({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        datefilterType: 'bookFrom'
      });
      
      const dashboardResponse = await fetch(
        `${apiUrl}/api/dashboardreports/fleet-operation-dashboard?${dashboardParams}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `${token}`,
          },
        }
      );
      
      const dashboardData = await dashboardResponse.json();
      
      let bookings = [];
      if (dashboardData?.statusCode === "0100") {
        bookings = dashboardData?.dataValues?.dataList || [];
        setAllBookings(bookings);
        
        setApiDataValues({
          totalDeliveryCharges: dashboardData?.dataValues?.totalDeliveryCharges || 0,
          totalPickCharges: dashboardData?.dataValues?.totalPickCharges || 0,
          totalPendingDelivery: dashboardData?.dataValues?.totalPendingDelivery || 0,
          totalPickupPending: dashboardData?.dataValues?.totalPickupPending || 0,
          totalLateDelivery: dashboardData?.dataValues?.totalLateDelivery || 0,
          dataListCount: dashboardData?.dataValues?.dataListCount || bookings.length
        });
      } else {
        setAllBookings([]);
        setApiDataValues({
          totalDeliveryCharges: 0,
          totalPickCharges: 0,
          totalPendingDelivery: 0,
          totalPickupPending: 0,
          totalLateDelivery: 0,
          dataListCount: 0
        });
      }
      
      // 2. Fetch fleet analytics CSV URL
      try {
        const analyticsParams = new URLSearchParams({
          datefilterType: 'bookFrom',
          startDate: dateRange.startDate,
          endDate: dateRange.endDate
        });
        
        const analyticsResponse = await fetch(
          `${apiUrl}/api/reports/fleet-analytics?${analyticsParams}`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
              Authorization: `${token}`,
            },
          }
        );
        
        const analyticsData = await analyticsResponse.json();
        
        // 3. If we got a CSV URL, fetch and parse it
        if (analyticsData?.statusCode === "0100" && analyticsData?.entities?.Location) {
          const csvUrl = analyticsData.entities.Location;
          const csvResponse = await fetch(csvUrl);
          const csvText = await csvResponse.text();
          const parsedData = parseCSV(csvText);
          setAnalyticsData(parsedData);
          
          // You can use this data for additional insights
          console.log("Analytics CSV Data:", parsedData);
          
          // Optionally: Use analytics data to enhance dashboard
          // For example, show additional metrics or charts
          showToast(`Analytics data loaded: ${parsedData.length} records`, 'success');
        }
      } catch (analyticsError) {
        console.warn("Failed to fetch analytics data:", analyticsError);
        // Don't fail the whole dashboard if analytics fails
      }
      
      setLastUpdated(new Date());
      
    } catch (error) {
      console.error("Error fetching fleet data:", error);
      setAllBookings([]);
      setApiError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFleetData();
  }, [dateRange.startDate, dateRange.endDate]);

  // Handler for booking row click
  const handleBookingClick = (booking) => {
    window.location.href = `/viewallbookings?bookingid=${booking.bookingCode}&display=true`;
  };

  // Extract unique sources and statuses from bookings
  const uniqueSources = useMemo(() => {
    return Array.from(new Set(allBookings.map(b => b.city).filter(Boolean)));
  }, [allBookings]);

  const uniqueStatuses = useMemo(() => {
    return Array.from(new Set(allBookings.map(b => b.bookingStatus).filter(Boolean)));
  }, [allBookings]);

  // FIX 1: Calculate active fleet count properly (unique vehicles)
  const activeFleet = useMemo(() => {
    const activeVehicles = new Set();
    allBookings.forEach(booking => {
      if ((booking.bookingStatus === 'Booking Confirmed' || booking.bookingStatus === 'Enroute') && booking.vehicleRegistrationNo) {
        activeVehicles.add(booking.vehicleRegistrationNo);
      }
    });
    return activeVehicles.size;
  }, [allBookings]);

  // Calculate statistics from bookings
  const stats = useMemo(() => {
    const totalRevenue = allBookings.reduce((sum, b) => {
      const revenue = (b.extraKmCharges || 0) + 
                      (b.extraHrCharges || 0) + 
                      (b.deliveryCharges || 0) + 
                      (b.pickupCharges || 0);
      return sum + revenue;
    }, 0);
    
    const totalBookings = allBookings.length;
    const uniqueHubs = new Set(allBookings.map(b => b.city)).size;
    // Use the properly calculated active fleet
    const activeFleetCount = activeFleet;
    const fleetSize = allBookings.filter(b => 
      b.bookingStatus !== 'Booking Closed' && 
      b.bookingStatus !== 'Refund Pending' &&
      b.bookingStatus !== 'Booking Rescheduled'
    ).length;
    
    const averageRevenue = totalBookings > 0 ? totalRevenue / totalBookings : 0;
    const utilizationRate = totalBookings > 0 ? (activeFleetCount / totalBookings) * 100 : 0;
    
    return {
      totalRevenue,
      totalBookings,
      activeHubs: uniqueHubs,
      fleetSize,
      averageRevenue,
      utilizationRate,
      activeFleet: activeFleetCount // This ensures active fleet is properly passed
    };
  }, [allBookings, activeFleet]);

  // Penalties summary
  const penaltiesSummary = useMemo(() => {
    return {
      totalOverSpeed: allBookings.reduce((sum, b) => sum + (b.overSpeed || 0), 0),
      totalDamage: allBookings.reduce((sum, b) => sum + (b.damage || 0), 0),
      totalFuel: allBookings.reduce((sum, b) => sum + (b.fuel || 0), 0),
      totalFastag: allBookings.reduce((sum, b) => sum + (b.fasTag || 0), 0),
      totalTrafficViolations: allBookings.reduce((sum, b) => sum + (b.trafficViolations || 0), 0),
      totalDowntimeCharges: allBookings.reduce((sum, b) => sum + (b.downtimeCharges || 0), 0),
      totalLateDeliveries: allBookings.filter(b => b.isLateDelivery === 'Yes').length,
    };
  }, [allBookings]);

  // Hub distribution from bookings
  const hubDistribution = useMemo(() => {
    const distribution = {};
    allBookings.forEach(b => {
      const source = b.city || "Unknown Hub";
      if (!distribution[source]) distribution[source] = { count: 0, amount: 0 };
      distribution[source].count++;
      distribution[source].amount += (b.extraKmCharges || 0) + (b.extraHrCharges || 0) + (b.deliveryCharges || 0) + (b.pickupCharges || 0);
    });
    return Object.entries(distribution).map(([name, data], idx) => ({
      name, ...data,
      color: `hsl(${idx * 45 + 200}, 70%, 50%)`
    })).sort((a, b) => b.amount - a.amount);
  }, [allBookings]);

  // Status distribution from bookings
  const statusDistribution = useMemo(() => {
    const distribution = {};
    const colorMap = {
      'Trip Completed': '#10b981',
      'Booking Confirmed': '#3b82f6',
      'Enroute': '#f59e0b',
      'Refund Pending': '#ef4444',
      'Document Pending': '#8b5cf6',
      'Booking Closed': '#64748b',
      'Booking Rescheduled': '#ec4899',
      'Booking Extented': '#14b8a6',
      'Verification Pending': '#f97316'
    };
    allBookings.forEach(b => {
      const status = b.bookingStatus || 'Unknown';
      if (!distribution[status]) distribution[status] = { count: 0, amount: 0 };
      distribution[status].count++;
      distribution[status].amount += (b.extraKmCharges || 0) + (b.extraHrCharges || 0) + (b.deliveryCharges || 0) + (b.pickupCharges || 0);
    });
    return Object.entries(distribution).map(([name, data]) => ({
      name, ...data,
      color: colorMap[name] || '#64748b'
    })).sort((a, b) => b.count - a.count);
  }, [allBookings]);

  // Monthly trend
  const monthlyTrend = useMemo(() => {
    const trends = {};
    allBookings.forEach(b => {
      const date = new Date(b.bookFrom);
      if (Number.isNaN(date.getTime())) return;
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      if (!trends[monthKey]) {
        trends[monthKey] = { 
          month: date.toLocaleString('default', { month: 'short' }), 
          amount: 0, 
          count: 0, 
          key: monthKey 
        };
      }
      trends[monthKey].amount += (b.extraKmCharges || 0) + (b.extraHrCharges || 0) + (b.deliveryCharges || 0) + (b.pickupCharges || 0);
      trends[monthKey].count++;
    });
    return Object.values(trends).sort((a, b) => a.key.localeCompare(b.key)).slice(-6);
  }, [allBookings]);

  // Funnel data
  const funnelData = useMemo(() => {
    const total = allBookings.length;
    const active = allBookings.filter(b => b.bookingStatus === 'Booking Confirmed' || b.bookingStatus === 'Enroute').length;
    const completed = allBookings.filter(b => b.bookingStatus === 'Trip Completed').length;
    const pendingDocs = allBookings.filter(b => b.bookingStatus === 'Document Pending' || b.bookingStatus === 'Verification Pending').length;
    
    return [
      { stage: "Total bookings", count: total, percentage: 100, color: "#1e293b" },
      { stage: "Active bookings", count: active, percentage: total > 0 ? (active / total) * 100 : 0, color: "#3b82f6" },
      { stage: "Document/Verification Pending", count: pendingDocs, percentage: total > 0 ? (pendingDocs / total) * 100 : 0, color: "#8b5cf6" },
      { stage: "Completed trips", count: completed, percentage: total > 0 ? (completed / total) * 100 : 0, color: "#10b981" }
    ];
  }, [allBookings]);

  // Top hubs
  const topHubs = useMemo(() => {
    const hubMap = {};
    allBookings.forEach(b => {
      const source = b.city || "Unknown";
      if (!hubMap[source]) {
        hubMap[source] = { 
          name: source, 
          revenue: 0, 
          bookings: 0,
          avgRevenue: 0
        };
      }
      hubMap[source].revenue += (b.extraKmCharges || 0) + (b.extraHrCharges || 0) + (b.deliveryCharges || 0) + (b.pickupCharges || 0);
      hubMap[source].bookings++;
    });
    return Object.values(hubMap)
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5)
      .map((hub, idx, arr) => ({
        ...hub,
        id: idx,
        avgRevenue: hub.bookings > 0 ? hub.revenue / hub.bookings : 0,
        growth: idx > 0 && arr[idx-1]?.revenue > 0 ? ((hub.revenue - arr[idx-1]?.revenue) / (arr[idx-1]?.revenue || 1) * 100) : 0
      }));
  }, [allBookings]);

  // Filtered bookings
  const filteredBookings = useMemo(() => {
    let result = [...allBookings];
    if (statusFilter !== "all") result = result.filter(b => b.bookingStatus === statusFilter);
    if (sourceFilter !== "all") result = result.filter(b => b.city === sourceFilter);
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      result = result.filter(b => 
        (b.bookingCode || '').toLowerCase().includes(term) || 
        (b.city || '').toLowerCase().includes(term) ||
        (b.vehicleName || '').toLowerCase().includes(term) ||
        (b.vehicleRegistrationNo || '').toLowerCase().includes(term) ||
        (b.fleetExecutiveName || '').toLowerCase().includes(term)
      );
    }
    if (sortBy === "newest") result.sort((a, b) => new Date(b.bookFrom) - new Date(a.bookFrom));
    else if (sortBy === "oldest") result.sort((a, b) => new Date(a.bookFrom) - new Date(b.bookFrom));
    else if (sortBy === "highest-revenue") {
      result.sort((a, b) => {
        const revenueA = (a.extraKmCharges || 0) + (a.extraHrCharges || 0) + (a.deliveryCharges || 0) + (a.pickupCharges || 0);
        const revenueB = (b.extraKmCharges || 0) + (b.extraHrCharges || 0) + (b.deliveryCharges || 0) + (b.pickupCharges || 0);
        return revenueB - revenueA;
      });
    }
    return result;
  }, [allBookings, statusFilter, sourceFilter, searchTerm, sortBy]);

  // Add this function
  const handleDownloadAnalytics = () => {
    if (analyticsData.length === 0) {
      showToast('No analytics data available. Please sync first.', 'error');
      return;
    }
    
    try {
      // Convert to CSV and download
      const headers = Object.keys(analyticsData[0]);
      const csvContent = [
        headers.join(','),
        ...analyticsData.map(row => 
          headers.map(h => {
            const value = row[h] || '';
            return `"${String(value).replace(/"/g, '""')}"`;
          }).join(',')
        )
      ].join('\n');
      
      const blob = new Blob(['\uFEFF' + csvContent], { 
        type: 'text/csv;charset=utf-8;' 
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `Fleet_Analytics_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      showToast('Analytics report downloaded successfully!', 'success');
    } catch (error) {
      console.error('Error downloading analytics:', error);
      showToast('Failed to download analytics data', 'error');
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    const escapeCSV = (val) => {
      if (val === undefined || val === null) return '""';
      const cleanStr = String(val).replace(/"/g, '""');
      return cleanStr.includes(',') || cleanStr.includes('"') || cleanStr.includes('\n') ? `"${cleanStr}"` : cleanStr;
    };

    const headers = [
      'Booking Code', 'Booking Status', 'City', 'Vehicle', 'Registration', 
      'Fleet Executive', 'Check-in', 'Check-out', 'Booking Hours', 'Total Days',
      'Total KM', 'Free KM', 'Extra KM Charges', 'Extra Hour Charges', 
      'Delivery Charges', 'Pickup Charges', 'Total Revenue',
      'Over Speed', 'Damage', 'Fuel', 'Fastag', 'Traffic Violations', 'Total Penalties',
      'Is Late Delivery', 'Late Delivery Time', 'Delivery Type', 'Pickup Type'
    ];
    
    const rows = filteredBookings.map(b => {
      const totalRevenue = (b.extraKmCharges || 0) + (b.extraHrCharges || 0) + (b.deliveryCharges || 0) + (b.pickupCharges || 0);
      const totalPenalties = (b.overSpeed || 0) + (b.damage || 0) + (b.fuel || 0) + (b.fasTag || 0) + (b.trafficViolations || 0) + (b.downtimeCharges || 0);
      return [
        b.bookingCode || '', b.bookingStatus || '', b.city || '',
        b.vehicleName || '', b.vehicleRegistrationNo || '',
        b.fleetExecutiveName || '',
        formatDateTime(b.bookFrom), formatDateTime(b.bookTo),
        b.bookingHrs || 0, b.totalNoOfDays || 0,
        b.tripOdometer || 0, b.overallFreeKm || 0,
        b.extraKmCharges || 0, b.extraHrCharges || 0, b.deliveryCharges || 0, b.pickupCharges || 0,
        totalRevenue,
        b.overSpeed || 0, b.damage || 0, b.fuel || 0, b.fasTag || 0, b.trafficViolations || 0,
        totalPenalties,
        b.isLateDelivery || 'No', b.lateDeliveryTime || 0,
        b.deliveryType || 'N/A', b.pickupType || 'N/A'
      ];
    });

    const csvContent = [headers, ...rows].map(r => r.map(escapeCSV).join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `Fleet_Operations_Export_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const SkeletonState = () => (
    <div className="space-y-6 animate-pulse">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
        <div className="md:col-span-5 h-36 bg-slate-200 rounded-2xl" />
        <div className="md:col-span-7 grid grid-cols-3 gap-4">
          <div className="bg-slate-200 rounded-xl h-36" /><div className="bg-slate-200 rounded-xl h-36" /><div className="bg-slate-200 rounded-xl h-36" />
        </div>
      </div>
      <div className="h-24 bg-slate-200 rounded-xl" />
      <div className="h-44 bg-slate-200 rounded-xl" />
      <div className="h-64 bg-slate-200 rounded-xl" />
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-sans text-slate-800 my-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            Fleet Operations Dashboard
            <InfoTooltip content="Comprehensive dashboard for monitoring fleet operations, bookings, revenue, and penalties. All metrics are updated based on the selected date range." position="bottom"/>
          </h1>
          <p className="text-sm text-gray-500 mt-1">Monitor fleet performance, bookings, and operational metrics.</p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {/* Custom DateRange Picker */}
          <DateRangePicker 
            dateRange={dateRange}
            setDateRange={setDateRange}
            onApply={fetchFleetData}
          />
          
          <span className="text-xs text-gray-400 font-medium">Last updated {Math.floor((new Date() - lastUpdated) / 60000)}m ago</span>
          <button 
            onClick={fetchFleetData}
            disabled={loading}
            className="p-2 bg-white hover:bg-slate-100 text-slate-600 rounded-xl border border-slate-200 shadow-xs flex items-center gap-1.5 text-xs font-semibold transition-colors"
          >
            <i className={`ti ti-refresh ${loading ? 'animate-spin text-blue-500' : ''}`} /> Refresh
          </button>
        </div>
      </div>

      {apiError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-6 flex items-center justify-between shadow-xs">
          <p className="text-red-700 text-xs font-medium flex items-center gap-2">
            <i className="ti ti-alert-triangle text-sm" /> Failed to fetch fleet data. Please check your connection and try again.
          </p>
          <button onClick={fetchFleetData} className="px-3 py-1 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-xs font-bold transition-all">
            Retry
          </button>
        </div>
      )}

      {loading ? <SkeletonState /> : (
        <>
          <StatisticsCards 
            stats={stats} 
            monthlyTrend={monthlyTrend} 
            deliveryStats={apiDataValues}
            penaltiesSummary={penaltiesSummary}
          />
          
          <ChartsRow 
            hubDistribution={hubDistribution}
            statusDistribution={statusDistribution}
          />
          
          <OperationsFunnel funnelData={funnelData} />
          
          <TopHubsWidget topHubs={topHubs} />
          
          <FiltersBar 
            searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            statusFilter={statusFilter} setStatusFilter={setStatusFilter}
            sourceFilter={sourceFilter} setSourceFilter={setSourceFilter}
            sortBy={sortBy} setSortBy={setSortBy}
            uniqueStatuses={uniqueStatuses} uniqueSources={uniqueSources}
            handleExportCSV={handleExportCSV} disableExport={filteredBookings.length === 0}
          />

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-200 flex justify-center items-center">
              <span className="text-xs font-medium text-gray-500">
                Showing {filteredBookings.length} of {allBookings.length} bookings
              </span>
              <InfoTooltip content="Click on any row to expand and view detailed information including trip details, odometer readings, financial breakdown, penalties, addresses, and comments." position="bottom" />
            </div>
            {filteredBookings.length === 0 ? (
              <div className="py-16 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-50 rounded-full mb-3 border border-slate-100">
                  <i className="ti ti-check text-slate-400 text-xl" />
                </div>
                <h3 className="text-sm font-bold text-slate-700 mb-0.5">No bookings found</h3>
                <p className="text-xs text-gray-400">Adjust your filters to see more results.</p>
              </div>
            ) : (
              <>
                <OperationsTable 
                  bookings={filteredBookings.slice(0, visibleCount)}
                  expandedRowId={expandedRowId}
                  toggleRowExpansion={(id) => setExpandedRowId(expandedRowId === id ? null : id)}
                  handleRowKeyDown={(e, id) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setExpandedRowId(expandedRowId === id ? null : id);
                    }
                  }}
                  onBookingClick={handleBookingClick}
                />
                
                {filteredBookings.length > visibleCount && (
                  <div className="p-3 bg-slate-50/50 text-center border-t border-slate-100">
                    <button 
                      onClick={() => setVisibleCount(prev => prev + 10)}
                      className="px-4 py-1.5 bg-white hover:bg-slate-50 text-slate-700 font-semibold border border-slate-200 rounded-lg text-xs shadow-xs transition-colors"
                    >
                      Load more ({filteredBookings.length - visibleCount} remaining)
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </>
      )}

      {toast && (
        <div className="fixed bottom-6 right-6 z-50 animate-in slide-in-from-bottom-5 fade-in duration-300">
          <div className="rounded-xl bg-slate-900 text-white font-medium shadow-xl px-4 py-3 flex items-center gap-2.5 text-xs border border-slate-800">
            <i className="ti ti-circle-check text-emerald-400 text-sm" />
            <span>{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default FleetOperationDashboard;